// ============================================
// SELECTORCLIENTE.JS — dropdown buscable de clientes_grupos
// Persiste selección en localStorage 'beep_cliente_tienda'.
// ============================================

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { User, ChevronDown, X } from 'lucide-react';
import { tokens } from '../styles/tokens';
import {
  API_BASE_CLI, PP, COLOR_OSCURO, COLOR_GRIS, COLOR_GRIS_CLARO, COLOR_BORDE,
  estiloInput,
} from './helpersClientes';
import { authHeaders } from '../utils/storage';

const STORAGE_KEY = 'beep_cliente_tienda';
const EVENT_NAME = 'beep-cliente-tienda-change';

export function leerClienteStorage() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
}

export function setClienteGlobal(cliente) {
  try {
    if (cliente == null) localStorage.removeItem(STORAGE_KEY);
    else localStorage.setItem(STORAGE_KEY, JSON.stringify(cliente));
  } catch {}
  window.dispatchEvent(new CustomEvent(EVENT_NAME, { detail: { cliente } }));
}

export function useClienteTienda() {
  const [cliente, setCliente] = useState(leerClienteStorage());
  useEffect(() => {
    const onChange = (e) => setCliente(e?.detail?.cliente ?? leerClienteStorage());
    window.addEventListener(EVENT_NAME, onChange);
    return () => window.removeEventListener(EVENT_NAME, onChange);
  }, []);
  return [cliente, setClienteGlobal];
}

export default function SelectorCliente({ compacto = false, onCambio }) {
  const [grupos, setGrupos] = useState([]);
  const [abierto, setAbierto] = useState(false);
  const [busq, setBusq] = useState('');
  const [cliente, setCliente] = useClienteTienda();
  const ref = useRef(null);

  useEffect(() => {
    fetch(`${API_BASE_CLI}/api/clientes/grupos`, { headers: authHeaders() })
      .then(x => x.json())
      .then(r => { if (r.ok) setGrupos(r.grupos || []); })
      .catch(() => {});
  }, []);

  useEffect(() => {
    const onClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setAbierto(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  const filtrados = useMemo(() => {
    if (!busq.trim()) return grupos.slice(0, 50);
    const s = busq.trim().toLowerCase();
    return grupos.filter(g => (g.nombre || '').toLowerCase().includes(s)).slice(0, 50);
  }, [grupos, busq]);

  const seleccionar = (g) => {
    const sel = g ? { id: g.id, nombre: g.nombre } : null;
    setCliente(sel);
    setAbierto(false);
    setBusq('');
    onCambio && onCambio(sel);
  };

  return (
    <div ref={ref} style={{ position: 'relative', minWidth: compacto ? 180 : 240, fontFamily: PP }}>
      <button
        onClick={() => setAbierto(a => !a)}
        style={{
          width: '100%',
          padding: compacto ? '6px 10px' : '8px 12px',
          fontSize: compacto ? 12 : 13,
          textAlign: 'left',
          border: `1px solid ${COLOR_BORDE}`,
          borderRadius: 8,
          background: '#fff',
          color: cliente ? COLOR_OSCURO : COLOR_GRIS_CLARO,
          fontFamily: PP, cursor: 'pointer',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8,
        }}
      >
        <span style={{ display: 'flex', alignItems: 'center', gap: 6, overflow: 'hidden' }}>
          <User size={14} strokeWidth={2} style={{ flexShrink: 0 }} />
          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {cliente ? cliente.nombre : 'Seleccionar cliente…'}
          </span>
        </span>
        <ChevronDown size={16} strokeWidth={2} style={{ color: COLOR_GRIS_CLARO, flexShrink: 0 }} />
      </button>
      {abierto && (
        <div style={{
          position: 'absolute', zIndex: 100, top: '100%', left: 0, right: 0,
          marginTop: 4, background: '#fff', border: `1px solid ${COLOR_BORDE}`,
          borderRadius: 8, boxShadow: '0 4px 14px rgba(0,0,0,0.08)',
          maxHeight: 360, overflow: 'hidden', display: 'flex', flexDirection: 'column',
        }}>
          <div style={{ padding: 8, borderBottom: `1px solid ${COLOR_BORDE}` }}>
            <input
              autoFocus
              style={{ ...estiloInput, fontSize: 12 }}
              placeholder="Buscar cliente…"
              value={busq}
              onChange={(e) => setBusq(e.target.value)}
            />
          </div>
          <div style={{ overflowY: 'auto', flex: 1, minHeight: 0 }}>
            {cliente && (
              <div
                onClick={() => seleccionar(null)}
                style={{
                  display: 'flex', alignItems: 'center', gap: 6,
                  padding: '8px 12px', fontSize: 12, color: tokens.danger,
                  cursor: 'pointer', borderBottom: `1px solid ${COLOR_BORDE}`,
                }}
              >
                <X size={12} strokeWidth={2} /> Quitar selección
              </div>
            )}
            {filtrados.map(g => (
              <div
                key={g.id}
                onClick={() => seleccionar(g)}
                style={{
                  padding: '8px 12px', fontSize: 13, cursor: 'pointer',
                  background: cliente?.id === g.id ? tokens.primarySoft : 'transparent',
                  color: COLOR_GRIS,
                }}
                onMouseEnter={(e) => { if (cliente?.id !== g.id) e.currentTarget.style.background = tokens.surfaceSubtle; }}
                onMouseLeave={(e) => { if (cliente?.id !== g.id) e.currentTarget.style.background = 'transparent'; }}
              >
                {g.nombre}
              </div>
            ))}
            {filtrados.length === 0 && (
              <div style={{ padding: 12, fontSize: 12, color: COLOR_GRIS_CLARO, textAlign: 'center' }}>
                Sin resultados
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
